import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

type CourtDecisionsFeatureProps = {
  variant?: "section" | "inline";
};

const decisions = [
  {
    shortLabel: "Umorzenie bez planu spłaty",
    title:
      "Umorzenie długów bez planu spłaty - Sąd Rejonowy w Gliwicach, październik 2026",
    image: "/manus-storage/obwieszczenie-umorzenie-gliwice_6aaff4bf.png",
    imageAlt:
      "Obwieszczenie postanowienia sądu o umorzeniu zobowiązań w sprawie prowadzonej przez Upadłości Ekspert",
    pdf: "/dokumenty/postanowienie-umorzenie-bez-planu-splaty.pdf",
    width: 772,
    height: 871,
  },
  {
    shortLabel: "Plan spłaty wierzycieli",
    title:
      "Plan spłaty tylko kosztów postępowania (ok. 300 zł miesięcznie przez 28 miesięcy), a potem umorzenie wszystkich długów - Sąd Rejonowy w Opolu, maj 2026",
    image: "/manus-storage/obwieszczenie-plan-splaty-opole_d63e7d5d.png",
    imageAlt:
      "Obwieszczenie postanowienia sądu o umorzeniu zobowiązań w sprawie prowadzonej przez Upadłości Ekspert",
    pdf: "/dokumenty/postanowienie-plan-splaty.pdf",
    width: 772,
    height: 886,
  },
];

export function CourtDecisionsFeature({
  variant = "section",
}: CourtDecisionsFeatureProps) {
  const [selected, setSelected] = useState<(typeof decisions)[number] | null>(
    null
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const inline = variant === "inline";

  const showPrevious = () => {
    setActiveIndex(index => (index - 1 + decisions.length) % decisions.length);
  };

  const showNext = () => {
    setActiveIndex(index => (index + 1) % decisions.length);
  };

  const carousel = (
    <div
      className="court-decisions-carousel"
      aria-roledescription="karuzela"
      aria-label="Dwa postanowienia sądów z naszych spraw"
    >
      <div className="court-decisions-viewport">
        <div
          className="court-decisions-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {decisions.map(decision => (
            <article className="court-decision-slide" key={decision.title}>
              <button
                className="court-decision-preview"
                type="button"
                onClick={() => setSelected(decision)}
                aria-label={`Powiększ dokument: ${decision.title}`}
              >
                <img
                  src={decision.image}
                  alt={decision.imageAlt}
                  width={decision.width}
                  height={decision.height}
                  loading="lazy"
                  decoding="async"
                />
              </button>
              <div className="court-decision-slide-footer">
                <h3>{decision.shortLabel}</h3>
                <a
                  className="court-decision-pdf-link"
                  href={decision.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Otwórz PDF
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div
        className="court-decisions-controls"
        aria-label="Sterowanie obwieszczeniami"
      >
        <button type="button" onClick={showPrevious}>
          Poprzednie
        </button>
        <div
          className="court-decisions-pagination"
          aria-label="Wybór obwieszczenia"
        >
          {decisions.map((decision, index) => (
            <button
              aria-current={activeIndex === index ? "true" : undefined}
              aria-label={`Pokaż dokument: ${decision.shortLabel}`}
              className={activeIndex === index ? "is-active" : ""}
              key={decision.title}
              onClick={() => setActiveIndex(index)}
              type="button"
            >
              {index + 1}
            </button>
          ))}
        </div>
        <button type="button" onClick={showNext}>
          Następne
        </button>
      </div>
    </div>
  );

  return (
    <>
      {inline ? (
        <div
          id="postanowienia-sadow"
          className="court-decisions court-decisions--inline"
          aria-label="Postanowienia sądów z naszych spraw"
        >
          {carousel}
        </div>
      ) : (
        <section
          id="postanowienia-sadow"
          className="court-decisions section-pad"
          aria-labelledby="court-decisions-title"
        >
          <div className="shell-wide">
            <div className="section-lead court-decisions-lead">
              <span className="section-kicker">
                przykładowe postanowienia sądu
              </span>
              <h2 id="court-decisions-title">
                Postanowienia sądów z naszych spraw
              </h2>
            </div>
            {carousel}
            <p className="court-decisions-privacy">
              Dane osobowe ukryliśmy z szacunku dla prywatności naszych
              Klientów.
            </p>
          </div>
        </section>
      )}
      {selected &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="court-decision-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Powiększone postanowienie sądu"
            onClick={() => setSelected(null)}
          >
            <div
              className="court-decision-lightbox-content"
              onClick={event => event.stopPropagation()}
            >
              <button
                className="court-decision-lightbox-close"
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Zamknij powiększenie"
              >
                <X size={22} />
              </button>
              <img
                src={selected.image}
                alt={selected.imageAlt}
                width={selected.width}
                height={selected.height}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
